import { Signal, signal } from '@angular/core';
import { FieldTree, SchemaPathTree, debounce, max, min, required } from '@angular/forms/signals';
import { GeneralACLoadDTO, GeneralDCLoadDTO, LoadDto, PFDto } from '../../../core/mceapi.client.generated';
import { appOpt, getNoneOptionDto, getSomeOptionDto } from '../../../core/mceapi.client.helpers';
import { match } from '../../../shared/functions/functional';
import { buildPowerFactorSchema, createPowerFactor, PowerFactor } from '../common/power-factor/power-factor.model';

export interface Load {
  key: string,
  case: string, // ac or dc
  ib: number,
  pf: PowerFactor,
  efficiency: number;
}

export function buildLoadSchema(path: SchemaPathTree<Load>) {

  required(path.ib, { message: `Current is required` });
  min(path.ib, 0, { message: `Current must be positive` });
  debounce(path.ib, 500);

  required(path.efficiency, { message: `Efficiency is required` });
  min(path.efficiency, 0, { message: `Efficiency must be positive` });
  max(path.efficiency, 1, { message: `Efficiency must be less than or equal to 1` });

  buildPowerFactorSchema(path.pf);

}

const getI = (load: LoadDto): number => {
  switch (load.case) {
    case 'dc': return match(() => 100, (x: GeneralDCLoadDTO) => x.ib ?? 100)(appOpt(load.generalDCLoad ?? {}));
    case 'ac': return match(() => 100, (x: GeneralACLoadDTO) => x.ib ?? 100)(appOpt(load.generalACLoad ?? {}));
    default: throw new Error(`Unsupported load case '${load.case}'.`);
  }
};

const getPF = (load: LoadDto): PFDto => {
  let pfDto = { pf: 1, lagging: true } as PFDto;
  switch (load.case) {
    case 'dc': return pfDto;
    case 'ac': return match(
      () => pfDto,
      (x: GeneralACLoadDTO) => ({ pf: x.pf ?? 1, lagging: x.lagging ?? true }))
      (appOpt(load.generalACLoad ?? {}));
    default: throw new Error(`Unsupported load case '${load.case}'.`);
  }
};

export function createLoad(load: LoadDto) {
  if (load.case !== 'dc' && load.case !== 'ac') {
    throw new Error(`Unsupported load case '${load.case}'.`);
  }

  const efficiency = load.efficiency?.point;
  if (efficiency === undefined) {
    throw new Error('Load DTO is missing efficiency.');
  }

  return signal<Load>({
    key: load.key ?? '',
    case: load.case,
    ib: getI(load),
    pf: createPowerFactor(getPF(load))(),
    efficiency
  });
}

export function createLoadDto(load: FieldTree<Load>): Signal<LoadDto> {
  let base = {
    key: load.key().value(),
    case: load.case().value(),
    generalDCLoad: getNoneOptionDto<GeneralDCLoadDTO>(),
    generalACLoad: getNoneOptionDto<GeneralACLoadDTO>(),
    efficiency: { point: load.efficiency().value() }
  } as LoadDto;

  switch (base.case) {
    case 'dc': return signal<LoadDto>({
      ...base, generalDCLoad: getSomeOptionDto<GeneralDCLoadDTO>({ ib: load.ib().value() })
    });
    case 'ac': return signal<LoadDto>({
      ...base, generalACLoad: getSomeOptionDto<GeneralACLoadDTO>(
        {
          ib: load.ib().value(),
          pf: load.pf().value().pf,
          lagging: load.pf().value().lagging
        })
    });
  }

  throw new Error(`Unsupported load case '${base.case}'.`);
}
